import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { SearchInput } from 'app/common/presentation'
import { useConnections } from 'app/connections/application'
import { ConnectionCategory } from 'app/connections/types'

import { PageBanner, Tabs } from 'components'

import { mapToUrl } from '../connectionUrlMapper'
import styles from './BrowseConnections.module.scss'
import ConnectionCard, { ConnectionCardConnection } from './ConnectionCard'

type Filter = {
  category: ConnectionCategory | 'all'
  count: number
  label: string
}

const BrowseConnections = () => {
  const { t } = useTranslation('connections')
  const navigate = useNavigate()
  const { availableConnections, connections } = useConnections()
  const [search, setSearch] = useState('')

  const enrichedAvailableConnections = useMemo<ConnectionCardConnection[]>(
    () =>
      availableConnections?.map((connection) => {
        const description = t(`available.${connection.name}.description`)

        return {
          ...connection,
          description: description.substring(0, description.indexOf('.') + 1) ?? '',
          displayName: t(`available.${connection.name}.displayName`),
        }
      }) ?? [],
    [availableConnections, t],
  )

  const filteredConnections = useMemo(
    () =>
      enrichedAvailableConnections.filter((connection) =>
        connection.displayName.toLowerCase().includes(search.toLowerCase()),
      ),
    [enrichedAvailableConnections, search],
  )

  const categoryFilters = useMemo<Filter[]>(
    () => [
      {
        category: 'all',
        count: filteredConnections.length,
        label: t('overview.browse.filter.all'),
      },
      ...filteredConnections.reduce<Filter[]>((filters, connection) => {
        const existingFilter = filters.find(({ category }) => category === connection.category)

        if (existingFilter) {
          existingFilter.count += 1
        } else {
          filters.push({
            category: connection.category,
            count: 1,
            label: t(`category.${connection.category}`),
          })
        }

        return filters
      }, []),
    ],
    [filteredConnections, t],
  )

  const [selectedCategory, setSelectedCategory] = useState(categoryFilters[0])

  useEffect(() => setSelectedCategory(categoryFilters[0]), [categoryFilters])

  return (
    <>
      <PageBanner subtitle={t('overview.description')} title={t('overview.headline')} />
      <div className={styles.toolbar}>
        <Tabs
          className={styles.filter}
          data={categoryFilters}
          onTabSelect={setSelectedCategory}
          renderContent={(filter, selected) => (
            <div className={styles.tab}>
              {filter.label}
              <span className={selected ? styles.selectedCount : styles.count}>{filter.count}</span>
            </div>
          )}
          selected={selectedCategory}
        />
        <SearchInput
          className={styles.search}
          onChange={setSearch}
          placeholder={t('overview.browse.search')}
          value={search}
        />
      </div>
      <div className={styles.connections}>
        {filteredConnections
          .filter(
            ({ category }) =>
              selectedCategory.category === 'all' || category === selectedCategory.category,
          )
          .map((connection) => (
            <ConnectionCard
              connection={connection}
              connected={connections.some(({ name }) => name === connection.name)}
              key={connection.name}
              onConnect={() => void navigate(`/connections/${mapToUrl(connection.name)}`)}
              onManage={() => void navigate(`/connections/${mapToUrl(connection.name)}/manage`)}
            />
          ))}
      </div>
    </>
  )
}

export default BrowseConnections
